<template>
  <aside class="global-sidebar">
    <div class="sidebar-brand bg-no-repeat bg-cover"></div>

    <div v-if="$slots.extra" class="sidebar-section">
      <slot name="extra" />
    </div>
    <div v-if="hasExtraSlot" class="sidebar-divider" />

    <nav class="sidebar-nav">
      <router-link to="/" custom v-slot="{ href, navigate }">
        <a
          :href="href"
          class="sidebar-item"
          :class="{ active: isExactHome }"
          @click="navigate"
        >
          <img
            class="sidebar-icon"
            :src="
              isExactHome
                ? require('../../assets/images/tabs/tab_1_active.png')
                : require('../../assets/images/tabs/tab_1.png')
            "
            alt="首页"
          />
          <span class="sidebar-label">首页</span>
        </a>
      </router-link>
      <router-link to="/generate" custom v-slot="{ isActive, href, navigate }">
        <a
          :href="href"
          class="sidebar-item"
          :class="{ active: isActive }"
          @click="navigate"
        >
          <img
            class="sidebar-icon"
            :src="
              isActive
                ? require('../../assets/images/tabs/tab_2_active.png')
                : require('../../assets/images/tabs/tab_2.png')
            "
            alt="生成"
          />
          <span class="sidebar-label">生成</span>
        </a>
      </router-link>
      <router-link to="/records" custom v-slot="{ isActive, href, navigate }">
        <a
          :href="href"
          class="sidebar-item"
          :class="{ active: isActive }"
          @click="navigate"
        >
          <img
            class="sidebar-icon"
            :src="
              isActive
                ? require('../../assets/images/tabs/tab_3_active.png')
                : require('../../assets/images/tabs/tab_3.png')
            "
            alt="工作流"
          />
          <span class="sidebar-label">历史记录</span>
        </a>
      </router-link>
      <router-link
        v-if="isAdmin"
        to="/workflows"
        class="sidebar-item"
        active-class="active"
      >
        <span class="sidebar-icon">📜</span>
        <span class="sidebar-label">工作流</span>
      </router-link>
      <router-link
        v-if="isAdmin"
        to="/users"
        class="sidebar-item"
        active-class="active"
      >
        <span class="sidebar-icon">👥</span>
        <span class="sidebar-label">用户管理</span>
      </router-link>
    </nav>

    <div class="sidebar-bottom">
      <template v-if="isAuthenticated">
        <span class="user-name">{{ account }}</span>
        <button class="logout-btn" @click="$emit('logout')">退出</button>
      </template>
      <button v-else class="login-btn" @click="$emit('login')">登录</button>
    </div>
  </aside>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { getAccount } from "../../services/auth";

export default defineComponent({
  name: "Sidebar",
  props: {
    isAuthenticated: { type: Boolean, default: false },
  },
  emits: ["logout", "login"],
  setup(props, { slots }) {
    const route = useRoute();
    const role = ref(localStorage.getItem("aigc_role") || "");
    const isAdmin = computed(() => role.value === "admin");
    const hasExtraSlot = computed(() => !!slots.extra);
    const account = computed(() => getAccount() || "");
    const isExactHome = computed(() => route.path === "/");

    watch(
      () => route.path,
      () => {
        role.value = localStorage.getItem("aigc_role") || "";
      },
    );

    return { isAdmin, hasExtraSlot, account, isExactHome };
  },
});
</script>

<style lang="less" scoped>
@import "../../assets/styles/variables.less";

.global-sidebar {
  width: @sidebar-width;
  background: @sidebar-bg;
  padding: 20px 12px;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  overflow-y: auto;
}

.sidebar-brand {
  margin: 0 auto;
  width: 62px;
  height: 62px;
  background-image: url("../../assets/images/logo.png");
}

.brand-text {
  font-size: 20px;
  font-weight: 700;
  color: @sidebar-text;
}

.sidebar-section {
  nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}

.sidebar-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 12px 0;
}

.sidebar-nav {
  margin-top: 80px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sidebar-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: @sidebar-text;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  width: 100%;
  box-sizing: border-box;
  text-align: left;

  &:hover {
    background: @sidebar-hover-bg;
  }

  &.active {
    background: transparent;
    color: #fb7299;
  }
}

.sidebar-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;

  &:not(img) {
    font-size: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

.sidebar-label {
  font-size: 14px;
  line-height: 1.2;
}

.sidebar-bottom {
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.user-name {
  font-size: 11px;
  color: @sidebar-text;
  opacity: 0.7;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-btn {
  padding: 4px 12px;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  color: @sidebar-text;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #ef4444;
    border-color: #ef4444;
  }
}

.login-btn {
  padding: 6px 16px;
  background: @sidebar-active-bg;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
